import { Box, Button, styled, useMediaQuery, useTheme } from '@mui/material'
import type { FC, PropsWithChildren } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import { COLORS, LinkUnstyled, remUnit, unit, Z_INDEX } from '@hcr/ui'

import { isNotUndefined } from '../../../../../../libs/utils/src/lib/objects'
import { SvgLogoHolidayClubWithLoma } from '../../../assets/images'
import { LINK_CONFIG } from '../../../configs'
import { ReferenceName, useSkipNavigation, useUserDetails } from '../../../contexts'
import { LayoutBase } from '../layout-base'

import { Navigation, NavigationAccountButton, NavigationFloating } from './common'
import { NavigationAccountButtonUnauthenticated } from './common/navigation-account-button-unauthenticated'
import { NavigationMenu, NavigationMenuUnauthenticated } from './common/navigation-menu'

const TOP_NAVIGATION_HEIGHT = 15
const BOTTOM_NAVIGATION_HEIGHT = 17

interface LayoutNavigationProps extends PropsWithChildren {
  showContextMenuButton?: boolean
  showSideNavigation?: boolean
  stretchContentVertically?: boolean
}

export const LayoutNavigation: FC<LayoutNavigationProps> = ({
  children,
  showContextMenuButton = false,
  showSideNavigation = true,
  stretchContentVertically = false,
}) => {
  const { t } = useTranslation()
  const userDetails = useUserDetails()

  const { scrollToAndFocus, registerRef } = useSkipNavigation()

  const focusRef = registerRef(ReferenceName.SIDE_NAVIGATION)
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const theme = useTheme()
  const location = useLocation()

  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const skipContentReferenceName: string =
    location.pathname === '/account' ? ReferenceName.ACCOUNT_BACK_BUTTON : ReferenceName.SIDE_NAVIGATION
  const handleSkipContent = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault()
    scrollToAndFocus(skipContentReferenceName)
  }

  return (
    <Box component='header'>
      <TopNavigationContainer>
        {isVisible && userDetails.isSuccess && isNotUndefined(userDetails.data) ? (
          <NavigationMenu setIsVisible={setIsVisible} isVisible={isVisible} />
        ) : (
          <NavigationMenuUnauthenticated setIsVisible={setIsVisible} isVisible={isVisible} />
        )}
        <SkipLink
          aria-label={t('navigation.skip-content')}
          onClick={handleSkipContent}
          to={`#${skipContentReferenceName}`}
        >
          {t('navigation.skip-content')}
        </SkipLink>
        <TopNavigationButton
          aria-label={t('navigation.main-page')}
          component={LinkUnstyled}
          to={LINK_CONFIG.webshop}
          target='_blank'
          rel='external'
        >
          <SvgLogoHolidayClubWithLoma width={unit(48.1925)} height={unit(8.61)} fill={COLORS.black} />
        </TopNavigationButton>
        {userDetails.isSuccess && isNotUndefined(userDetails.data) ? (
          <NavigationAccountButton
            setIsVisible={setIsVisible}
            isVisible={isVisible}
            firstName={userDetails.data.first_name}
            lastName={userDetails.data.last_name}
          />
        ) : (
          <NavigationAccountButtonUnauthenticated setIsVisible={setIsVisible} isVisible={isVisible} />
        )}
      </TopNavigationContainer>
      <LayoutBase>
        {showSideNavigation && (
          <SideNavigationContainer>
            <SideNavigationContent>
              <SideNavigation as='nav'>
                <Navigation variant='vertical' ref={isMobile ? null : focusRef} />
              </SideNavigation>
            </SideNavigationContent>
          </SideNavigationContainer>
        )}
        <NavigationFloating showContextMenuButton={showContextMenuButton} />
        {stretchContentVertically ? (
          <ContentStretched as='main'>{children}</ContentStretched>
        ) : (
          <Content as='main'>{children}</Content>
        )}
      </LayoutBase>
      {showSideNavigation && (
        <BottomNavigation>
          <Navigation variant='horizontal' ref={isMobile ? focusRef : null} />
        </BottomNavigation>
      )}
    </Box>
  )
}

const SkipLink = styled(LinkUnstyled)`
  position: absolute;
  top: -100px;
  left: 0;
  z-index: ${Z_INDEX.skipContent};
  text-decoration: none;
  color: ${COLORS.white};
  background-color: ${COLORS.burgundi[500]};
  text-transform: uppercase;
  width: ${unit(48.5)};
  height: ${unit(12)};
  font-size: ${remUnit(3)};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${unit(2.5)};

  :focus {
    top: calc((${unit(15)} - ${unit(12)}) / 2);
  }
`

const TopNavigationContainer = styled(Box)`
  background-color: ${COLORS.burgundi[500]};
  height: ${unit(TOP_NAVIGATION_HEIGHT)};
  z-index: ${Z_INDEX.navigation};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 ${unit(5)} 0 ${unit(5.75)};
  ${props => props.theme.breakpoints.up('lg')} {
    padding: 0 ${unit(8.5)} 0 ${unit(8.5)};
  }

  ${props => props.theme.breakpoints.up('xl')} {
    padding: 0 ${unit(36)} 0 ${unit(36)};
  }
`

const TopNavigationButton = styled(Button)`
  display: inline-flex;
  gap: ${unit(1.5)};
  color: ${COLORS.white};
  font-size: ${remUnit(3)};
  font-weight: initial;
  width: fit-content;
  height: ${unit(11)};
  padding: 0;
` as typeof Button

const SideNavigationContainer = styled(Box)`
  position: sticky;
  top: 0;
  z-index: ${Z_INDEX.navigation};

  ${props => props.theme.breakpoints.down('md')} {
    display: none;
  }
`

const SideNavigationContent = styled(Box)`
  position: absolute;
  left: ${unit(-23)};
  top: ${unit(4)};
`

const SideNavigation = styled(Box)`
  border-radius: ${unit(1)};
`

const Content = styled(Box)`
  ${props => props.theme.breakpoints.down('md')} {
    padding-bottom: ${unit(BOTTOM_NAVIGATION_HEIGHT)};
  }
`

const ContentStretched = styled(Box)`
  height: calc(100vh - ${unit(TOP_NAVIGATION_HEIGHT)});

  ${props => props.theme.breakpoints.down('md')} {
    padding-bottom: ${unit(BOTTOM_NAVIGATION_HEIGHT)};
  }

  ${props => props.theme.breakpoints.up('md')} {
    padding-bottom: ${unit(TOP_NAVIGATION_HEIGHT)};
  }
`

const BottomNavigation = styled(Box)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: ${unit(BOTTOM_NAVIGATION_HEIGHT)};
  z-index: ${Z_INDEX.navigation};

  ${props => props.theme.breakpoints.up('md')} {
    display: none;
  }
`
